import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  serializeCSV, csvToObjects, downloadCSV, buildTemplate, readFileAsText
} from "@/lib/csvUtils";

/**
 * Reusable CSV import/export controls for admin configuration tables.
 * Props:
 *  - rows: current data array
 *  - columns: [{ key, label, get?(row), parse?(string) }]
 *  - filename: base file name (no extension)
 *  - onImport: (parsedObjects) => void
 *  - testIdPrefix: optional, prefix for data-testid attributes
 */
export const CsvIOButtons = ({ rows, columns, filename, onImport, testIdPrefix = "csv" }) => {
  const fileRef = useRef(null);

  const handleExport = () => {
    const csv = serializeCSV(rows, columns);
    downloadCSV(`${filename}.csv`, csv);
    toast.success("Esportazione completata", { description: `${rows.length} righe in ${filename}.csv` });
  };

  const handleTemplate = () => {
    downloadCSV(`${filename}_template.csv`, buildTemplate(columns));
    toast.success("Template scaricato", { description: `${filename}_template.csv` });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const parsed = csvToObjects(text, columns);
      if (parsed.length === 0) {
        toast.error("Nessuna riga importata", { description: "Verifica intestazioni e contenuto del file." });
      } else {
        onImport(parsed);
        toast.success("Import completato", { description: `${parsed.length} righe importate.` });
      }
    } catch {
      toast.error("Errore durante l'import");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={handleTemplate}
        className="gap-1"
        data-testid={`${testIdPrefix}-template-btn`}
      >
        <FileDown className="h-3.5 w-3.5" /> Template
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        className="gap-1"
        data-testid={`${testIdPrefix}-import-btn`}
      >
        <Upload className="h-3.5 w-3.5" /> Importa CSV
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="gap-1"
        data-testid={`${testIdPrefix}-export-btn`}
      >
        <Download className="h-3.5 w-3.5" /> Esporta CSV
      </Button>
    </div>
  );
};

export default CsvIOButtons;
